import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['thresholdType', 'minValue', 'maxValueContainer', 'destroyInput']

  declare thresholdTypeTargets: HTMLSelectElement[]
  declare maxValueContainerTargets: HTMLElement[]
  declare minValueTargets: HTMLInputElement[]
  declare destroyInputTarget: HTMLInputElement

  connect (): void {
    this.updateMaxValueVisibility()
    this.updateMinValuePlaceholder()

    document.addEventListener('threshold:confirm-remove', this.handleConfirmRemove.bind(this) as EventListener)
  }

  disconnect (): void {
    document.removeEventListener('threshold:confirm-remove', this.handleConfirmRemove.bind(this) as EventListener)
  }

  collectOptions (event: { target: HTMLSelectElement }): void {
    const select = event.target
    const index = this.thresholdTypeTargets.indexOf(select)

    if (index >= 0) {
      const maxValueContainer = this.maxValueContainerTargets[index]
      const minValue = this.minValueTargets[index]

      if (select.value === 'range') {
        maxValueContainer.classList.remove('hidden')
        minValue.placeholder = 'From'
      } else {
        maxValueContainer.classList.add('hidden')
        minValue.placeholder = 'Enter a value'
      }
    } else {
      this.updateMaxValueVisibility()
      this.updateMinValuePlaceholder()
    }
  }

  updateMaxValueVisibility (): void {
    this.thresholdTypeTargets.forEach((select, index) => {
      if (index < this.maxValueContainerTargets.length) {
        const maxValueContainer = this.maxValueContainerTargets[index]

        if (select.value === 'range') {
          maxValueContainer.classList.remove('hidden')
        } else {
          maxValueContainer.classList.add('hidden')
        }
      }
    })
  }

  updateMinValuePlaceholder (): void {
    this.thresholdTypeTargets.forEach((select, index) => {
      if (index < this.minValueTargets.length) {
        const minValue = this.minValueTargets[index]

        if (select.value === 'range') {
          minValue.placeholder = 'From'
        } else {
          minValue.placeholder = 'Enter a value'
        }
      }
    })
  }

  async remove (event: Event): Promise<void> {
    event.preventDefault()

    const id = this.findThresholdId()

    if (id == null) {
      this.destroyInputTarget.value = 'true'
      this.element.classList.add('hidden')
      return
    }

    try {
      const response = await fetch(`/risks/settings/score_configurations/check_threshold_usage?id=${id}`, {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      const data = await response.json()

      if (data.used === true) {
        const modal = document.getElementById('threshold-delete-confirmation-modal')
        if (modal !== null) {
          const hiddenInput = modal.querySelector("input[name='threshold_id']") as HTMLInputElement
          if (hiddenInput !== null) {
            hiddenInput.value = id
          }
          this.openModal(modal)
        }
      } else {
        this.destroyInputTarget.value = 'true'
        this.element.classList.add('hidden')
      }
    } catch (error) {
      console.error('Error checking threshold usage:', error)
    }
  }

  handleConfirmRemove (event: CustomEvent): void {
    const thresholdId = event.detail.thresholdId
    if (thresholdId != null && thresholdId !== '') {
      this.removeThresholdById(thresholdId)
    }

    const modal = document.getElementById('threshold-delete-confirmation-modal')
    if (modal !== null) {
      const modalController = this.application.getControllerForElementAndIdentifier(
        modal, 'modal'
      ) as { close: () => void } | null

      if (modalController !== null) {
        modalController.close()
      }
    }
  }

  openModal (modal: HTMLElement): void {
    const modalController = this.application.getControllerForElementAndIdentifier(
      modal,
      'modal'
    ) as { open: () => void } | null

    if (modalController !== null) {
      modalController.open()
    }
  }

  confirmRemove (event: Event): void {
    event.preventDefault()

    const modal = document.getElementById('threshold-delete-confirmation-modal')
    if (modal === null) return

    const hiddenInput = modal.querySelector("input[name='threshold_id']") as HTMLInputElement
    if (hiddenInput === null) return

    const thresholdId = hiddenInput.value ?? ''
    if (thresholdId !== '') {
      this.removeThresholdById(thresholdId)
      hiddenInput.value = ''
    }

    this.closeModal()
  }

  removeThresholdById (thresholdId: string): void {
    const thresholdItem = document.querySelector(`[data-threshold-id="${thresholdId}"]`)
    if (thresholdItem !== null) {
      const destroyInput = thresholdItem.querySelector("input[name$='[_destroy]']") as HTMLInputElement
      if (destroyInput !== null) {
        destroyInput.value = 'true'
        thresholdItem.classList.add('hidden')
      }
    }
  }

  closeModal (): void {
    const modal = document.getElementById('threshold-delete-confirmation-modal')
    if (modal === null) return

    const modalController = this.application.getControllerForElementAndIdentifier(
      modal,
      'modal'
    ) as { close: () => void } | null

    if (modalController !== null) {
      modalController.close()
    }
  }

  findThresholdId (): string | null {
    const idInput = this.element.querySelector("input[name$='[id]']") as HTMLInputElement
    return idInput?.value ?? null
  }

  getCSRFToken (): string {
    const metaTag = document.querySelector("meta[name='csrf-token']") as HTMLMetaElement
    return metaTag?.content ?? ''
  }
}
